import type { BoundingBoxConfig } from '@examdojo/core/widget';
import type { GeometryElement, GeometryPadParams } from '../geometrypad.model';
import { resolveElementOrder } from '../elements/element.resolver';
import { PortRegistry, registerSolvedPortsOnRegistry } from '../port';
import { canonicalPlace } from './canonical-placement';
import type { PlacedShape } from './placement.model';
import { applyTransform, resolveVertex } from './placement.model';

/** Default padding around shapes (in coordinate units) */
const DEFAULT_PADDING = 2;
/** Minimum visible range in each dimension */
const MIN_RANGE = 4;

const ANNOTATION_TYPES = new Set(['LengthAnnotation', 'ArrowAnnotation']);

/**
 * Compute a bounding box that fits all placed shapes with padding.
 *
 * @param placedShapes All placed shapes with world-space vertices
 * @param padding Padding around the shapes in coordinate units
 * @returns BoundingBoxConfig for the coordinate system
 */
export function computeBoundingBox(
  placedShapes: Map<string, PlacedShape>,
  padding: number = DEFAULT_PADDING,
): BoundingBoxConfig {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const placed of placedShapes.values()) {
    for (const vertexSource of Object.values(placed.vertices)) {
      const [x, y] = resolveVertex(vertexSource);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }

  // Handle empty or single-point case
  if (!isFinite(minX)) {
    return { xStart: -5, xEnd: 5, yStart: -5, yEnd: 5 };
  }

  // Ensure minimum range
  const rangeX = Math.max(maxX - minX, MIN_RANGE);
  const rangeY = Math.max(maxY - minY, MIN_RANGE);

  // Center the range if it was expanded
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  return {
    xStart: centerX - rangeX / 2 - padding,
    xEnd: centerX + rangeX / 2 + padding,
    yStart: centerY - rangeY / 2 - padding,
    yEnd: centerY + rangeY / 2 + padding,
  };
}

/**
 * Compute the auto bounding box for a GeometryPad configuration by solving
 * all shapes, resolving positions, and fitting the result with padding.
 *
 * This is the same pipeline used by `GeometryPadWidget.init()` but extracted
 * so the editor can show (and let the user tweak) the computed values.
 */
export function computeAutoBoundingBox(params: GeometryPadParams): BoundingBoxConfig {
  const portRegistry = new PortRegistry();

  // Register variables
  if (params.variables) {
    for (const [name, value] of Object.entries(params.variables)) {
      const v = value;
      portRegistry.registerVariable(name, () => v);
    }
  }

  // Convert elements and resolve order
  const elements: GeometryElement[] = Object.entries(params.elements).map(([id, def]) => ({ ...def, id }));
  const sortedElements = resolveElementOrder(elements);

  // Incrementally solve, place, and register world-space ports
  const canonicalVertices = new Map<string, Record<string, [number, number]>>();
  const placedShapes = new Map<string, PlacedShape>();
  let autoLayoutMaxX = 0;
  const AUTO_LAYOUT_GAP = 3;

  for (const element of sortedElements) {
    if (ANNOTATION_TYPES.has(element.type)) {
      continue;
    }
    try {
      const result = canonicalPlace(element, portRegistry);
      canonicalVertices.set(element.id, result.vertices);
      registerSolvedPortsOnRegistry(element, result.solved, result.vertices, portRegistry);

      let placed: PlacedShape | undefined;
      if (result.isPositioned) {
        placed = {
          elementId: element.id,
          type: element.type,
          vertices: result.vertices,
          transform: result.transform ?? { rotation: 0, translation: [0, 0] },
          isReactive: false,
        };
      }

      if (!placed) {
        const canonical = result.vertices;
        if (Object.keys(canonical).length > 0) {
          let localMinX = Infinity;
          for (const coord of Object.values(canonical)) {
            localMinX = Math.min(localMinX, coord[0]);
          }
          const offsetX = autoLayoutMaxX + AUTO_LAYOUT_GAP - localMinX;
          const worldVertices: Record<string, [number, number]> = {};
          for (const [name, coord] of Object.entries(canonical)) {
            worldVertices[name] = [coord[0] + offsetX, coord[1]];
          }
          placed = {
            elementId: element.id,
            type: element.type,
            vertices: worldVertices,
            transform: { rotation: 0, translation: [offsetX, 0] },
            isReactive: false,
          };
          for (const coord of Object.values(worldVertices)) {
            autoLayoutMaxX = Math.max(autoLayoutMaxX, coord[0]);
          }
        }
      } else {
        for (const v of Object.values(placed.vertices)) {
          const coord = typeof v === 'function' ? v() : v;
          autoLayoutMaxX = Math.max(autoLayoutMaxX, coord[0]);
        }
      }

      if (placed) {
        placedShapes.set(element.id, placed);
        const worldVertices: Record<string, [number, number]> = {};
        for (const [name, coord] of Object.entries(result.vertices)) {
          worldVertices[name] = applyTransform(coord, placed.transform);
        }
        registerSolvedPortsOnRegistry(element, result.solved, worldVertices, portRegistry);
      }
    } catch {
      // Element couldn't be solved — skip
    }
  }

  return computeBoundingBox(placedShapes);
}
